import {
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  Intersection,
  MeshBasicMaterial,
  Object3D,
  PerspectiveCamera,
  Points,
  PointsMaterial,
  Raycaster,
  Vector3,
  Vector4,
} from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry';
import { Text } from 'troika-three-text';

import type { TooltipHeight } from '../../../react/components/skyview/tooltip/components/height-layers';
import { isShownBelow } from '../../../utils/three/visibility';
import { isDefined } from '../../../utils/variables';
import { AssetManager } from '../../asset-manager';
import { SkyviewCamera } from '../../cameras/types';
import { CoordinateManager } from '../../coordinate-manager';
import { Tiled3DModelHandle } from '../../geodata/3d-tiled-models/tiled-3d-model-handle';
import { PointCloudHandle } from '../../geodata/point-clouds/point-cloud-handle';
import { renderOrders } from '../../render-orders';
import { ViewerEnvironmentInstance } from '../../viewer-environment';
import { isAssetPickable } from '../select-tool';
import { getWorldPerPixel } from './world-per-pixel';

export type HeightLayerSource = 'pointCloud' | 'tiledModel' | 'asset';

/**
 * A surface straight above or below the snapped point, or the snapped point itself where it is on
 * none of them, e.g. at a height that is set.
 */
export type HeightLayer = {
  /** The height, in world coordinates. */
  z: number;
  source: HeightLayerSource | 'snap';
  /** The name of the asset that it is on. */
  name?: string;
  /** Whether the current point is snapped to it. */
  snapped: boolean;
};

/**
 * How far apart in height (meters) points of the point cloud are on different layers, e.g. the
 * ground and a bridge over it. The points of a layer spread in height where it slopes.
 */
const pointCloudLayerGap = 0.3;
/** Hits closer than this in height (meters) are one layer, e.g. where faces of a mesh meet. */
const sameHeight = 0.01;
/** How far from the snapped height (meters) a layer is the one that is snapped to. */
const snappedTolerance = 0.1;
/** How far from the vertical line (pixels) lines of assets are found. */
const linePixels = 4;
/**
 * How far from the vertical line (pixels) the points of the point cloud are found, and at least
 * and at most how far (meters), so that a sparse point cloud is found too.
 */
const pointCloudPixels = 12;
const pointCloudRadius = { min: 0.05, max: 2 };

/**
 * A height that is found, and how far from the vertical line it is, horizontally, e.g. for a point
 * of the point cloud. Hits on the line itself are 0.
 */
type Hit = { z: number; distance: number };

/**
 * Groups the hits, highest first, into layers where they are closer in height than the gap. The
 * height of a layer is interpolated at the line from its hits, the closest ones counting most.
 */
function groupHits(hits: Hit[], gap: number): number[] {
  const sorted = [...hits].sort((a, b) => b.z - a.z);
  const groups: Hit[][] = [];

  for (const hit of sorted) {
    const group = groups[groups.length - 1];

    if (group && group[group.length - 1].z - hit.z <= gap) {
      group.push(hit);
    } else {
      groups.push([hit]);
    }
  }

  return groups.map((group) => {
    // Inverse distance weighting, with a hit on the line counting the most.
    const weights = group.map((hit) => 1 / (hit.distance + 1e-3) ** 2);
    const total = weights.reduce((sum, weight) => sum + weight, 0);
    return group.reduce((sum, hit, i) => sum + hit.z * weights[i], 0) / total;
  });
}

/**
 * Gets how far a pixel is at the point, in meters, from how far apart a meter is on screen.
 */
function getMetersPerPixel(camera: SkyviewCamera, point: Vector3) {
  const screen = camera.getScreenPosition(point);
  const pixels = Math.max(
    screen.distanceTo(camera.getScreenPosition(point.clone().setX(point.x + 1))),
    screen.distanceTo(camera.getScreenPosition(point.clone().setY(point.y + 1))),
  );

  return pixels > 0 ? 1 / pixels : 1;
}

/**
 * Gets the intersections with the points of the loaded point cloud near the vertical line, also
 * where the point cloud is hidden but snapped to.
 */
function intersectPointCloud(rayCaster: Raycaster): Intersection[] {
  const pointCloud = PointCloudHandle.instance.activePointCloud;

  if (!pointCloud) {
    return [];
  }

  const intersections: Intersection[] = [];
  pointCloud.updateWorldMatrix(false, true);
  pointCloud.traverse((object) => {
    // Not the chunks that are culled, which are shown at another level of detail.
    if (object instanceof Points && isShownBelow(object, pointCloud)) {
      rayCaster.intersectObject(object, false, intersections);
    }
  });

  return intersections;
}

/**
 * Finds the surfaces straight above and below the snapped point: of the point cloud, the tiled
 * model and the shown assets, highest first. The snapped point is among them, as the layer it is
 * on or as its own.
 */
export function findHeightLayers(
  camera: SkyviewCamera,
  point: Vector3,
  predicate: (asset: Object3D) => boolean = isAssetPickable,
): HeightLayer[] {
  const metersPerPixel = getMetersPerPixel(camera, point);
  const rayCaster = new Raycaster(
    new Vector3(point.x, point.y, point.z + 100000),
    new Vector3(0, 0, -1),
  );
  rayCaster.params.Points.threshold = Math.min(
    Math.max(pointCloudPixels * metersPerPixel, pointCloudRadius.min),
    pointCloudRadius.max,
  );
  rayCaster.params.Line.threshold = linePixels * metersPerPixel;

  const shown = (x: Intersection) => isShownBelow(x.object);
  const toHit = (x: Intersection): Hit => ({ z: x.point.z, distance: x.distanceToRay ?? 0 });
  const layers = (source: HeightLayerSource, hits: Hit[], gap: number, name?: string) =>
    groupHits(hits, gap).map((z) => ({ z, source, name, snapped: false }));

  const found: HeightLayer[] = [
    ...layers('pointCloud', intersectPointCloud(rayCaster).map(toHit), pointCloudLayerGap),
    ...layers(
      'tiledModel',
      Tiled3DModelHandle.instance.pick(rayCaster).filter(shown).map(toHit),
      sameHeight,
    ),
    // Each asset on its own, for its name.
    ...AssetManager.instance.children
      .filter(predicate)
      .flatMap((asset) =>
        layers(
          'asset',
          rayCaster.intersectObject(asset, true).filter(shown).map(toHit),
          sameHeight,
          asset.name,
        ),
      ),
  ];

  const snapped = found.reduce<HeightLayer | undefined>(
    (closest, layer) =>
      Math.abs(layer.z - point.z) <= snappedTolerance &&
      (!closest || Math.abs(layer.z - point.z) < Math.abs(closest.z - point.z))
        ? layer
        : closest,
    undefined,
  );

  if (snapped) {
    snapped.snapped = true;
  } else {
    found.push({ z: point.z, source: 'snap', snapped: true });
  }

  return found.sort((a, b) => b.z - a.z);
}

/**
 * Gets the heights of the layers for the tooltip, in project coordinates, each with how much
 * higher it is than the next one below.
 */
export function toTooltipHeights(point: Vector3, layers: HeightLayer[]): TooltipHeight[] {
  const toProject = (z: number) =>
    CoordinateManager.getInstance().localToProjectCoords(point.clone().setZ(z)).height;

  return layers.map((layer, i) => ({
    height: toProject(layer.z),
    source: layer.source,
    name: layer.name,
    snapped: layer.snapped,
    delta: i < layers.length - 1 ? layer.z - layers[i + 1].z : undefined,
  }));
}

// Light blue, bright enough to stand out against the point cloud and the tiled model.
const guideColor = new Color(0x7cc8ff);
// Cyan, so that the differences in height stand out next to the line.
const deltaLabelColor = new Color(0x4dfaff);
/** The width of the line, and the length of the dashes and the gaps between them, in pixels. */
const lineWidthPx = 3;
const dashSizePx = 5;
const gapSizePx = 3;
const dotSizePx = 8;
const labelSizePx = 16;
/** How far beside the line (pixels) the labels are: heights to the right, deltas to the left. */
const labelOffsetPx = 10;

const labelMaterial = new MeshBasicMaterial({
  transparent: true,
  depthTest: false,
  depthWrite: false,
  toneMapped: false,
});

const tmpViewport = new Vector4();
const tmpRight = new Vector3();

/**
 * A label beside the line, the same size on screen, facing the camera.
 */
type Label = { text: Text; position: Vector3; side: 1 | -1 };

/**
 * All heights at the snapped point of a tool, when its option is on: the layers above and below
 * it are found as it moves, listed in the tooltip, and shown in the view as a dashed vertical line
 * through them, with a dot and the height at each, and the difference in height between them. The
 * line is only shown in a perspective view, where it isn't seen end on.
 *
 * It is placed in world coordinates, also when it is added to a tool that follows the cursor.
 */
export class HeightLayersGuide extends Object3D {
  /** Whether all heights are shown, from the option. */
  enabled = false;
  /** The layers at the snapped point, while all heights are shown. */
  heightLayers?: HeightLayer[];

  /** Where the dashes and labels get their size on screen. */
  private origin = new Vector3();
  private lineMaterial = new LineMaterial({
    color: guideColor.getHex(),
    linewidth: lineWidthPx,
    dashed: true,
    dashSize: dashSizePx,
    gapSize: gapSizePx,
    depthTest: false,
    depthWrite: false,
    transparent: true,
  });
  private line = new LineSegments2(new LineSegmentsGeometry(), this.lineMaterial);
  private dots = new Points(
    new BufferGeometry(),
    new PointsMaterial({
      color: guideColor,
      size: dotSizePx,
      sizeAttenuation: false,
      depthTest: false,
      depthWrite: false,
      transparent: true,
    }),
  );
  private labels: Label[] = [];

  constructor() {
    super();
    this.visible = false;
    this.matrixAutoUpdate = false;
    this.line.renderOrder = renderOrders.pickers.angleGuideLine;
    this.line.frustumCulled = false;
    this.dots.renderOrder = renderOrders.pickers.pickingLine;
    this.dots.frustumCulled = false;

    // Typed as an Object3D, since the types of LineSegments2 leave out the camera.
    (this.line as Object3D).onBeforeRender = (renderer, scene, camera) => {
      // The material needs the size of the viewport for the width in pixels.
      renderer.getViewport(tmpViewport);
      this.lineMaterial.resolution.set(tmpViewport.z, tmpViewport.w);

      // The distances along the line are in meters, the dashes in pixels.
      const worldPerPixel = getWorldPerPixel(renderer, camera, this.origin);
      this.lineMaterial.dashScale = worldPerPixel > 0 ? 1 / worldPerPixel : 1;
    };

    this.add(this.line, this.dots);
  }

  /**
   * Finds and shows the layers at the snapped point, or hides them without it. Call it after the
   * tool that it is added to has moved.
   */
  update(
    camera: SkyviewCamera,
    point: Vector3 | undefined,
    predicate?: (asset: Object3D) => boolean,
  ) {
    this.heightLayers =
      this.enabled && point ? findHeightLayers(camera, point, predicate) : undefined;
    this.show(camera, point, this.heightLayers);
  }

  /**
   * Gets the layers at the snapped point for the tooltip, while all heights are shown.
   */
  getTooltipHeights(point: Vector3 | undefined) {
    return point && this.heightLayers ? toTooltipHeights(point, this.heightLayers) : undefined;
  }

  hide() {
    this.heightLayers = undefined;
    this.visible = false;
  }

  private show(
    camera: SkyviewCamera,
    point: Vector3 | undefined,
    layers: HeightLayer[] | undefined,
  ) {
    this.visible =
      isDefined(point) &&
      (layers?.length ?? 0) > 1 &&
      camera.getTHREECamera() instanceof PerspectiveCamera;

    if (!this.visible || !point || !layers) {
      return;
    }

    this.origin.copy(point);
    const top = point.clone().setZ(layers[0].z);
    const bottom = point.clone().setZ(layers[layers.length - 1].z);
    this.line.geometry.setPositions([...top.toArray(), ...bottom.toArray()]);
    this.line.computeLineDistances();

    this.dots.geometry.setAttribute(
      'position',
      new Float32BufferAttribute(
        layers.flatMap((layer) => [point.x, point.y, layer.z]),
        3,
      ),
    );
    this.dots.geometry.computeBoundingSphere();

    // The height at each layer, and the difference in height halfway to the next one below.
    const unit = ViewerEnvironmentInstance.getUnitSymbols().length;
    const heights = toTooltipHeights(point, layers);
    this.showLabels([
      ...heights.map((row, i) => ({
        text: `${row.height.toFixed(2)} ${unit}`,
        position: point.clone().setZ(layers[i].z),
        side: 1 as const,
      })),
      ...heights.flatMap((row, i) =>
        isDefined(row.delta)
          ? [
              {
                text: `Δ ${row.delta.toFixed(2)} ${unit}`,
                position: point.clone().setZ((layers[i].z + layers[i + 1].z) / 2),
                side: -1 as const,
              },
            ]
          : [],
      ),
    ]);

    // Undoes where the parent is, e.g. a tool that follows the cursor.
    if (this.parent) {
      this.parent.updateWorldMatrix(true, false);
      this.matrix.copy(this.parent.matrixWorld).invert();
    } else {
      this.matrix.identity();
    }
    this.matrixWorldNeedsUpdate = true;
  }

  /**
   * Shows the labels, reusing the ones that were shown before, and hides the rest.
   */
  private showLabels(labels: { text: string; position: Vector3; side: 1 | -1 }[]) {
    while (this.labels.length < labels.length) {
      this.labels.push(this.createLabel());
    }

    this.labels.forEach((label, i) => {
      const shown = labels[i];
      label.text.visible = isDefined(shown);

      if (shown) {
        label.text.text = shown.text;
        label.text.anchorX = shown.side > 0 ? 'left' : 'right';
        label.text.color = shown.side > 0 ? 0xffffff : deltaLabelColor.getHex();
        label.position.copy(shown.position);
        label.side = shown.side;
      }
    });
  }

  /**
   * Creates a label beside the line, with the same size on screen, that faces the camera.
   */
  private createLabel(): Label {
    const text = new Text();
    const label: Label = { text, position: new Vector3(), side: 1 };
    text.fontSize = 1;
    text.fontWeight = 'bold';
    text.anchorY = 'middle';
    text.outlineColor = 0x000000;
    text.outlineOpacity = 0.9;
    text.outlineWidth = '25%';
    text.material = labelMaterial;
    text.renderOrder = renderOrders.pickers.angleGuideLabel;
    text.frustumCulled = false;

    const troikaOnBeforeRender = text.onBeforeRender.bind(text);

    text.onBeforeRender = (renderer, scene, camera, geometry, material, group) => {
      troikaOnBeforeRender(renderer, scene, camera, geometry, material, group);

      const worldPerPixel = getWorldPerPixel(renderer, camera, label.position);
      camera.getWorldQuaternion(text.quaternion);
      tmpRight.set(1, 0, 0).applyQuaternion(text.quaternion);
      text.position
        .copy(label.position)
        .addScaledVector(tmpRight, label.side * labelOffsetPx * worldPerPixel);
      text.scale.setScalar(labelSizePx * worldPerPixel);
      text.updateMatrixWorld();
    };

    this.add(text);
    return label;
  }
}
